/**
 * ContextPacksList — the account's Context Packs (curated bundles for
 * pitches/QBRs/decks). Sales AI loads packs by name (get_context_pack);
 * Export/Brief copy assembled markdown to the clipboard.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { NOTE_VIS_LABEL } from '@techstack/core';
import { useArmed } from './useAccountNotes';

function ContextPacksList({ api }) {
  const { packs, loadPack, copyExport, deletePack, removePackItem } = api;
  const [openPack, setOpenPack] = useState(null); // { id, items }
  const [armed, arm] = useArmed();

  const view = async (packId) => {
    if (openPack?.id === packId) { setOpenPack(null); return; }
    const items = await loadPack(packId);
    if (items) setOpenPack({ id: packId, items });
  };

  const removeItem = (packId, itemId) => {
    removePackItem.mutate({ packId, itemId }, {
      onSuccess: async () => {
        const items = await loadPack(packId);
        if (items) setOpenPack({ id: packId, items });
      },
    });
  };

  return (
    <>
      <div className="aw-notes-header" style={{ marginTop: 4 }}>
        <span className="aw-notes-title">Context Packs</span>
        <span className="aw-notes-count">{packs.length}</span>
      </div>
      {packs.length === 0 && (
        <p className="aw-none">No packs yet — select notes above and "Add to pack".</p>
      )}
      <div className="aw-notes-list">
        {packs.map((p) => (
          <div key={p.id} className="aw-note">
            <div className="aw-note-meta" style={{ marginTop: 0 }}>
              <strong>{p.name}</strong>
              {p.purpose && <span className="aw-note-tag">{p.purpose}</span>}
              <span className="aw-chip">{NOTE_VIS_LABEL[p.visibility] || p.visibility}</span>
              <span className="aw-note-author">{p.itemCount} item(s) · {p.author}</span>
              <span className="aw-notes-spacer" />
              <button type="button" className="aw-notes-chip" onClick={() => view(p.id)}>
                {openPack?.id === p.id ? 'Hide' : 'View'}
              </button>
              <button type="button" className="aw-notes-chip"
                title="Copy assembled markdown (paste into a doc or NotebookLM)"
                onClick={() => copyExport(p.id, 'export')}>Export</button>
              <button type="button" className="aw-notes-chip"
                title="Copy the meeting-prep brief (MEDDPICC coverage, gaps as a discovery agenda, next steps)"
                onClick={() => copyExport(p.id, 'brief')}>Brief</button>
              {p.mine && (
                <button type="button" className={`aw-notes-chip${armed[p.id] ? ' aw-armed' : ''}`}
                  title="Delete pack (source notes are untouched)"
                  onClick={() => arm(p.id, () => { if (openPack?.id === p.id) setOpenPack(null); deletePack.mutate(p.id); })}>
                  {armed[p.id] ? 'Sure?' : 'Delete'}
                </button>
              )}
            </div>
            {openPack?.id === p.id && (openPack.items || []).map((it) => (
              <div key={it.id} className="aw-note-action" style={{ display: 'flex', gap: 6 }}>
                <span style={{ flex: 1 }}>
                  {it.type === 'note'
                    ? <>{it.snapshot?.title ? <strong>{it.snapshot.title}: </strong> : null}{String(it.snapshot?.content || '').slice(0, 140)}{String(it.snapshot?.content || '').length > 140 ? '…' : ''}</>
                    : <em>{String(it.snapshot?.content || '').slice(0, 140)}{String(it.snapshot?.content || '').length > 140 ? '…' : ''}</em>}
                </span>
                <button type="button" className="aw-note-delete" title="Remove from pack"
                  onClick={() => removeItem(p.id, it.id)}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

export default ContextPacksList;
